import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useWishlist } from "@/lib/wishlist";
import { useAuth } from "@/lib/auth";
import { fetchProducts } from "@/lib/products";
import { ProductCard } from "@/components/site/ProductCard";

export const Route = createFileRoute("/wishlist")({
  head: () => ({
    meta: [
      { title: "Your Wishlist — KAKAIYS" },
      {
        name: "description",
        content:
          "The pieces you've saved at KAKAIYS — your private list of leather you intend to own.",
      },
      { property: "og:title", content: "Your Wishlist — KAKAIYS" },
      {
        property: "og:description",
        content: "The pieces you've saved at KAKAIYS.",
      },
    ],
  }),
  component: WishlistPage,
});

function WishlistPage() {
  const { user } = useAuth();
  const { slugs, loading } = useWishlist();
  const navigate = useNavigate();
  const router = useRouter();
  const handleBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      router.history.back();
    } else {
      navigate({ to: "/shop" });
    }
  };
  const { data: products = [] } = useQuery({
    queryKey: ["products", "active"],
    queryFn: () => fetchProducts(),
  });

  const items = products.filter((p) => slugs.has(p.slug));

  return (
    <>
      <section className="pt-32 md:pt-40 pb-12 container-luxe">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </button>
        <div className="text-center">
          <div className="eyebrow mb-4 text-gold">Saved For Later</div>
          <h1 className="font-serif text-5xl md:text-7xl">Your Wishlist.</h1>
          <p className="mt-6 max-w-xl mx-auto text-sm md:text-base text-muted-foreground">
            A quiet shelf. The pieces you've considered, kept for the moment they become inevitable.
          </p>
        </div>
      </section>

      <section className="container-luxe py-12 md:py-20">
        {!user ? (
          <div className="text-center py-20 max-w-md mx-auto">
            <p className="text-sm text-muted-foreground mb-6">
              Sign in to save and sync your wishlist across every device.
            </p>
            <Link
              to="/auth"
              search={{}}
              className="btn-gold inline-flex items-center justify-center px-8 py-3 text-xs uppercase tracking-[0.24em]"
            >
              Sign In
            </Link>
          </div>
        ) : loading ? (
          <div className="text-center text-sm text-muted-foreground py-20">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 max-w-md mx-auto">
            <p className="text-sm text-muted-foreground mb-6">
              Your wishlist is empty. Begin by tapping the heart on any piece that moves you.
            </p>
            <Link
              to="/shop"
              className="btn-gold inline-flex items-center justify-center px-8 py-3 text-xs uppercase tracking-[0.24em]"
            >
              Discover the Collection
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-5 gap-y-12 md:grid-cols-3 lg:grid-cols-4 md:gap-x-8">
            {items.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}
